import {
	DndContext,
	DragEndEvent,
	MouseSensor,
	UniqueIdentifier,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { noop } from 'lodash-es';
import { ReactNode, useEffect, useMemo } from 'react';

import { DraggableProps } from './Draggable';
import { useDraggable } from './useDraggable';

interface DraggableCollectionProps<P> {
	data: P[];
	dataKey: keyof P;
	renderer: (items: (P & DraggableProps)[]) => ReactNode;
	onDragEnd?: (items: P[keyof P][]) => void;
	onInitial?: () => Promise<P[keyof P][]>;
}

export const DraggableCollection = <P,>({
	data,
	dataKey,
	renderer,
	onDragEnd: onDragEndHandler = noop,
	onInitial: onInitialHandler,
}: DraggableCollectionProps<P>) => {
	const { items, dataMap, onDragEnd, onInitial } = useDraggable(
		data,
		dataKey
	);
	const sensors = useSensors(useSensor(MouseSensor, {}));

	useEffect(() => {
		if (!onInitialHandler) {
			return;
		}

		const onInitialHandlerAsync = async () => {
			onInitial(await onInitialHandler());
		};

		onInitialHandlerAsync();
	}, []);

	const handleDragEnd = (event: DragEndEvent) => {
		const newItems = onDragEnd(event);
		onDragEndHandler(newItems);
	};

	const rendererItems = useMemo(
		() =>
			renderer(
				items
					.map((item) => dataMap.get(item as keyof P))
					.filter((item) => item != undefined)
					.map((item) => ({
						...item,
						id: item[dataKey],
						transform: 'x',
					})) as (P & DraggableProps)[]
			),
		[items, dataMap, dataKey, renderer]
	);

	return (
		<DndContext
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
			accessibility={{ screenReaderInstructions: { draggable: '' } }}
			sensors={sensors}>
			<SortableContext
				items={items as UniqueIdentifier[]}
				strategy={horizontalListSortingStrategy}>
				{rendererItems}
			</SortableContext>
		</DndContext>
	);
};
